import { ShapeEditorMachineContext } from 'app/agriculture/forms/components/ShapeEditor/ShapeEditorMachine';
import * as turf from '@turf/helpers';
import nearestPointOnLine from '@turf/nearest-point-on-line';

const runSnappingEveryNthframe = 10;
const snappingSearchRadius = 30;

export function getSnappingShapes({
    snappingFrameCount,
    snappingShapes,
    windowPosition,
    viewer,
}: {
    snappingFrameCount: number;
    snappingShapes: Cesium.Cartesian3[][];
    windowPosition: Cesium.Cartesian2;
    viewer: Cesium.Viewer;
}) {
    if (snappingFrameCount % runSnappingEveryNthframe !== 0) {
        return snappingShapes;
    }
    // use drill pick to get some horrible cesium object array
    const picks = viewer.scene.drillPick(
        windowPosition,
        undefined,
        snappingSearchRadius,
        snappingSearchRadius,
    );
    // transform the drillPick result into an array of lines
    return picks
        .filter(
            (pick: {
                id?: {
                    snapping?: boolean;
                };
            }) => pick.id?.snapping === true,
        ) // this seems to stop self snapping. Seems like a hack
        .map(
            (pick: {
                primitive?: {
                    geometryInstances?: {
                        geometry?: {
                            _positions: Cesium.Cartesian3[];
                        };
                    };
                };
            }) => pick.primitive?.geometryInstances?.geometry?._positions,
        )
        .filter((x) => x != null);
}
export function snap({
    isSnappingEnabled,
    worldPosition,
    snappingShapes,
    viewer,
}: {
    isSnappingEnabled: boolean;
    worldPosition?: Cesium.Cartesian3;
    snappingShapes: Cesium.Cartesian3[][];
    viewer: Cesium.Viewer;
}) {
    if (!isSnappingEnabled || worldPosition == null) return worldPosition;
    // get the snapping distance in meters. This can change based on zoom, so needs to be calculated all the time
    const searchDistance = getSnapSearchDistance(viewer);
    // snap to all the corners in the nearby geometry
    const snapVertex = getNearestCorner(snappingShapes, worldPosition, searchDistance);
    return (
        snapVertex ??
        getNearestPointOnEdge(snappingShapes, worldPosition, searchDistance) ??
        worldPosition
    );
}

function getSnapSearchDistance(viewer?: Cesium.Viewer) {
    if (viewer == null) return 0;

    const leftRay = viewer.scene.camera.getPickRay(new Cesium.Cartesian2(0, 0));
    const rightRay = viewer.scene.camera.getPickRay(new Cesium.Cartesian2(snappingSearchRadius, 0));
    if (!leftRay || !rightRay) return 0;

    const leftPos = viewer.scene.globe.pick(leftRay, viewer.scene);
    const rightPos = viewer.scene.globe.pick(rightRay, viewer.scene);
    if (!leftPos || !rightPos) return 0;

    return Cesium.Cartesian3.distance(leftPos, rightPos);
}

function getNearestPointOnEdge(
    snapShapes: Cesium.Cartesian3[][],
    mouseLocation: Cesium.Cartesian3,
    searchDistance: number,
): undefined | Cesium.Cartesian3 {
    // convert all of our data into something turf can understand
    const location = Cesium.Ellipsoid.WGS84.cartesianToCartographic(mouseLocation);
    const point = turf.point([
        Cesium.Math.toDegrees(location.longitude),
        Cesium.Math.toDegrees(location.latitude),
    ]);
    const linesTransformed = turf.multiLineString(
        snapShapes.map((line) =>
            line.map((i) => {
                const location = Cesium.Ellipsoid.WGS84.cartesianToCartographic(i);
                return [
                    Cesium.Math.toDegrees(location.longitude),
                    Cesium.Math.toDegrees(location.latitude),
                ];
            }),
        ),
    );
    // use turf to calculate the nearest point to our geometry
    const nearestLinePoint = nearestPointOnLine(linesTransformed, point, {
        units: 'meters',
    });
    // if the nearest point is less that the search distance, return it, other return undefined
    if (
        nearestLinePoint.properties.dist &&
        nearestLinePoint.properties.dist < searchDistance &&
        nearestLinePoint.geometry.coordinates[0] != null &&
        nearestLinePoint.geometry.coordinates[1] != null
    ) {
        return Cesium.Cartesian3.fromDegrees(
            nearestLinePoint.geometry.coordinates[0],
            nearestLinePoint.geometry.coordinates[1],
        );
    }
    return undefined;
}

function getNearestCorner(
    snapShapes: Cesium.Cartesian3[][],
    mouseLocation: Cesium.Cartesian3,
    searchDistance: number,
) {
    let closestVertex: Cesium.Cartesian3 | undefined;

    for (const vert of snapShapes.flat()) {
        // for each vertex calculate the distance to the mouse
        const distance = Cesium.Cartesian3.distance(vert, mouseLocation);
        // if the distance is less than the search distance, it becomes the new search distance
        // and our guess for closest corner
        if (distance < searchDistance) {
            searchDistance = distance;
            closestVertex = vert;
        }
    }
    return closestVertex;
}
